// ═══════════════════════════════════════════════════════════
// Handoff Store — Full CRUD for events, notes, positions, actions
// V2 rebuild matching V1 feature parity
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { callClaude } from '../api/worker';
import { fetchIgnatiusUsers } from '../lib/api-switch';

const FE = FIELDS.handoffEvents;
const FN = FIELDS.handoffNotes;
const FA = FIELDS.handoffActionItems;
const FP = FIELDS.handoffPositions;

// ── Types ──────────────────────────────────────────────

export interface HandoffEvent {
  id: number;
  incidentId: number | null;
  eventName: string;
  status: string;
  createdByName: string;
  createdByEmail: string;
  datecreated?: string;
}

export interface HandoffNote {
  id: number;
  eventId: number;
  positionName: string;
  fromShift: string;
  toShift: string;
  senderName: string;
  senderEmail: string;
  recipientName: string;
  recipientEmail: string;
  sectionsJson: string;
  sections: Array<{ title: string; content: string }>;
  signature: string;
  status: string; // draft | submitted | acknowledged
  acknowledgedAt: string;
  datecreated?: string;
  aiSummary?: string; // in-memory only (generated via Claude)
  // Aliases matching types/index.ts HandoffNote fields
  positionId?: string;
  shiftDate?: string;
  senderSignature?: string;
  recipientSignature?: string;
  submittedAt?: string;
}

export interface ActionItem {
  id: number;
  eventId: number;
  noteId: number | null;
  title: string;
  description: string;
  assignedTo: string;
  assignedToEmail: string;
  priority: string;
  status: string; // open | in_progress | completed | cancelled
  dueDate: string;
  completedAt: string;
  notes: string;
  datecreated?: string;
}

export interface Position {
  id: number;
  eventId: number;
  positionName: string;
  shortName: string;
  dayShiftName: string;
  dayShiftEmail: string;
  nightShiftName: string;
  nightShiftEmail: string;
  sortOrder: string;
  // Aliases matching types/index.ts Position fields
  dayUserName?: string;
  dayUserEmail?: string;
  nightUserName?: string;
  nightUserEmail?: string;
  status?: string;
  datecreated?: string;
}

// ── Mappers ────────────────────────────────────────────

function parseSections(json: string): Array<{ title: string; content: string }> {
  if (!json) return [];
  try { return JSON.parse(json); } catch { return []; }
}

function mapEvent(r: Record<string, unknown>): HandoffEvent {
  return {
    id: r.record_id as number,
    incidentId: r[FE.incidentId] != null ? Number(r[FE.incidentId]) : null,
    eventName: (r[FE.eventName] as string) || '',
    status: (r[FE.status] as string) || 'active',
    createdByName: (r[FE.createdByName] as string) || '',
    createdByEmail: (r[FE.createdByEmail] as string) || '',
    datecreated: (r.created_at as string) || '',
  };
}

function mapNote(r: Record<string, unknown>): HandoffNote {
  const sectionsJson = (r[FN.sectionsJson] as string) || '';
  return {
    id: r.record_id as number,
    eventId: Number(r[FN.eventId]) || 0,
    positionName: (r[FN.positionName] as string) || '',
    fromShift: (r[FN.fromShift] as string) || '',
    toShift: (r[FN.toShift] as string) || '',
    senderName: (r[FN.senderName] as string) || '',
    senderEmail: (r[FN.senderEmail] as string) || '',
    recipientName: (r[FN.recipientName] as string) || '',
    recipientEmail: (r[FN.recipientEmail] as string) || '',
    sectionsJson,
    sections: parseSections(sectionsJson),
    signature: (r[FN.signature] as string) || '',
    status: (r[FN.status] as string) || 'draft',
    acknowledgedAt: (r[FN.acknowledgedAt] as string) || '',
    datecreated: (r.created_at as string) || '',
  };
}

function mapAction(r: Record<string, unknown>): ActionItem {
  return {
    id: r.record_id as number,
    eventId: Number(r[FA.eventId]) || 0,
    noteId: r[FA.noteId] != null ? Number(r[FA.noteId]) : null,
    title: (r[FA.title] as string) || '',
    description: (r[FA.description] as string) || '',
    assignedTo: (r[FA.assignedTo] as string) || '',
    assignedToEmail: (r[FA.assignedToEmail] as string) || '',
    priority: (r[FA.priority] as string) || 'normal',
    status: (r[FA.status] as string) || 'pending',
    dueDate: (r[FA.dueDate] as string) || '',
    completedAt: (r[FA.completedAt] as string) || '',
    notes: (r[FA.notes] as string) || '',
    datecreated: (r.created_at as string) || '',
  };
}

function mapPosition(r: Record<string, unknown>): Position {
  const dayShiftName = (r[FP.dayShiftName] as string) || '';
  const dayShiftEmail = (r[FP.dayShiftEmail] as string) || '';
  const nightShiftName = (r[FP.nightShiftName] as string) || '';
  const nightShiftEmail = (r[FP.nightShiftEmail] as string) || '';
  return {
    id: r.record_id as number,
    eventId: Number(r[FP.eventId]) || 0,
    positionName: (r[FP.positionName] as string) || '',
    shortName: '', // no V2 column for shortName
    dayShiftName,
    dayShiftEmail,
    nightShiftName,
    nightShiftEmail,
    sortOrder: (r[FP.sortOrder] as string) || '0',
    // Aliases for types/index.ts Position compatibility
    dayUserName: dayShiftName,
    dayUserEmail: dayShiftEmail,
    nightUserName: nightShiftName,
    nightUserEmail: nightShiftEmail,
    status: 'active',
    datecreated: (r.created_at as string) || '',
  };
}

// ── Default ICS Positions ──────────────────────────────

export const DEFAULT_ICS_POSITIONS = [
  { name: 'Incident Commander', short: 'IC', order: '1' },
  { name: 'Operations Section Chief', short: 'OPS', order: '2' },
  { name: 'Planning Section Chief', short: 'PLAN', order: '3' },
  { name: 'Logistics Section Chief', short: 'LOG', order: '4' },
  { name: 'Finance/Admin Section Chief', short: 'FIN', order: '5' },
  { name: 'Public Information Officer', short: 'PIO', order: '6' },
  { name: 'Safety Officer', short: 'SO', order: '7' },
  { name: 'Liaison Officer', short: 'LNO', order: '8' },
  { name: 'Communications', short: 'COMMS', order: '9' },
  { name: 'Situation Unit Leader', short: 'SITL', order: '10' },
];

export const DEFAULT_TEMPLATE_SECTIONS = [
  { title: 'Situation Overview', placeholder: 'Current situation and status...' },
  { title: 'Significant Activities', placeholder: 'Key events during this shift...' },
  { title: 'Ongoing Actions', placeholder: 'Actions in progress...' },
  { title: 'Issues & Concerns', placeholder: 'Problems or concerns to be aware of...' },
  { title: 'Recommendations for Next Shift', placeholder: 'Recommendations for incoming personnel...' },
];

// ── Store ──────────────────────────────────────────────

interface HandoffState {
  events: HandoffEvent[];
  notes: HandoffNote[];
  actions: ActionItem[];
  positions: Position[];
  loading: boolean;

  // Fetch
  fetchEvents: () => Promise<void>;
  fetchNotes: (eventId?: number) => Promise<void>;
  fetchActions: (eventId?: number) => Promise<void>;
  fetchPositions: (eventId?: number) => Promise<void>;
  fetchAll: () => Promise<void>;

  // Events
  createEvent: (data: Record<string, unknown>) => Promise<number | null>;

  // Notes
  createNote: (data: Record<string, unknown>) => Promise<number | null>;
  updateNote: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteNote: (id: number) => Promise<void>;
  submitNote: (noteId: number) => Promise<void>;
  acknowledgeNote: (noteId: number, recipientName: string) => Promise<void>;

  // Actions
  createAction: (data: Record<string, unknown>) => Promise<number | null>;
  updateAction: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteAction: (id: number) => Promise<void>;
  completeAction: (id: number) => Promise<void>;
  reopenAction: (id: number) => Promise<void>;

  // Positions
  createPosition: (data: Record<string, unknown>) => Promise<number | null>;
  updatePosition: (id: number, data: Record<string, unknown>) => Promise<void>;
  deletePosition: (id: number) => Promise<void>;
  assignPosition: (posId: number, shift: 'day' | 'night', name: string, email: string) => Promise<void>;
  clearPositionAssignment: (posId: number, shift: 'day' | 'night') => Promise<void>;

  // Helpers
  getPositionsForEvent: (eventId: string | number) => Position[];
  getNotesForEvent: (eventId: string | number) => HandoffNote[];
  getActionsForEvent: (eventId: string | number) => ActionItem[];

  // AI
  generateAISummary: (note: HandoffNote) => Promise<string>;
  generateActionsFromHandoff: (note: HandoffNote) => Promise<string>;

  // V1 compat aliases
  actionItems: any[];
  isLoading: boolean;
  rosterUsers: any[];
  loadNotes: (eventId?: number) => Promise<void>;
  loadActionItems: (eventId?: number) => Promise<void>;
  loadPositions: (eventId?: number) => Promise<void>;
  loadRosterUsers: () => Promise<void>;
  createActionItem: (data: any) => Promise<number | null>;
  deleteActionItem: (id: any) => Promise<void>;
  completeActionItem: (id: any) => Promise<void>;
  reopenActionItem: (id: any) => Promise<void>;
  generateBriefing: (eventData: any, notes: any[]) => Promise<string>;
  generatePriorities: (eventId: any) => Promise<any[]>;
  loadAll: () => Promise<void>;
  saveDraft: (data: any) => Promise<{ noteId: number | null }>;
}

export const useHandoffStore = create<HandoffState>((set, get) => ({
  events: [], notes: [], actions: [], positions: [], loading: false,

  // ── Fetch ──────────────────────────────────────────

  fetchEvents: async () => {
    try {
      const r = await fetchRecords(TABLES.handoffEvents, { limit: 200 });
      set({ events: (r.records || []).map(mapEvent) });
    } catch (e) { console.error('[Handoff] fetchEvents', e); }
  },

  fetchNotes: async (eventId?: number) => {
    try {
      const r = await fetchRecords(TABLES.handoffNotes, { limit: 500 });
      let records = (r.records || []).map(mapNote);
      if (eventId != null) {
        records = records.filter(n => n.eventId === eventId);
      }
      set({ notes: records });
    } catch (e) { console.error('[Handoff] fetchNotes', e); }
  },

  fetchActions: async (eventId?: number) => {
    try {
      const r = await fetchRecords(TABLES.handoffActionItems, { limit: 500 });
      let records = (r.records || []).map(mapAction);
      if (eventId != null) {
        records = records.filter(a => a.eventId === eventId);
      }
      const mapped = records;
      set({ actions: mapped, actionItems: mapped });
    } catch (e) { console.error('[Handoff] fetchActions', e); }
  },

  fetchPositions: async (eventId?: number) => {
    try {
      const r = await fetchRecords(TABLES.handoffPositions, { limit: 500 });
      let records = (r.records || []).map(mapPosition);
      if (eventId != null) {
        records = records.filter(p => p.eventId === eventId);
      }
      records.sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
      set({ positions: records });
    } catch (e) { console.error('[Handoff] fetchPositions', e); }
  },

  fetchAll: async () => {
    set({ loading: true });
    await Promise.all([get().fetchEvents(), get().fetchNotes(), get().fetchActions(), get().fetchPositions()]);
    set({ loading: false });
  },

  // ── Events ─────────────────────────────────────────

  createEvent: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.eventName) p[FE.eventName] = data.eventName;
    if (data.incidentId) p[FE.incidentId] = data.incidentId;
    if (data.status) p[FE.status] = data.status;
    if (data.createdByName) p[FE.createdByName] = data.createdByName;
    if (data.createdByEmail) p[FE.createdByEmail] = data.createdByEmail;
    try {
      const r = await createRecord<Record<string, unknown>>(TABLES.handoffEvents, p);
      await get().fetchEvents();
      return (r as any).record_id || (r as any).id || null;
    } catch (e) { console.error('[Handoff] createEvent', e); return null; }
  },

  // ── Notes ──────────────────────────────────────────

  createNote: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.eventId) p[FN.eventId] = data.eventId;
    if (data.positionName) p[FN.positionName] = data.positionName;
    if (data.fromShift) p[FN.fromShift] = data.fromShift;
    if (data.toShift) p[FN.toShift] = data.toShift;
    if (data.senderName) p[FN.senderName] = data.senderName;
    if (data.senderEmail) p[FN.senderEmail] = data.senderEmail;
    if (data.recipientName) p[FN.recipientName] = data.recipientName;
    if (data.sectionsJson) p[FN.sectionsJson] = data.sectionsJson;
    if (data.signature) p[FN.signature] = data.signature;
    p[FN.status] = data.status || 'draft';
    try {
      const r = await createRecord<Record<string, unknown>>(TABLES.handoffNotes, p);
      const eventId = data.eventId ? Number(data.eventId) : undefined;
      await get().fetchNotes(eventId);
      return (r as any).record_id || (r as any).id || null;
    } catch (e) { console.error('[Handoff] createNote', e); return null; }
  },

  updateNote: async (id, data) => {
    const p: Record<string, unknown> = {};
    if (data.positionName !== undefined) p[FN.positionName] = data.positionName;
    if (data.fromShift !== undefined) p[FN.fromShift] = data.fromShift;
    if (data.toShift !== undefined) p[FN.toShift] = data.toShift;
    if (data.senderName !== undefined) p[FN.senderName] = data.senderName;
    if (data.recipientName !== undefined) p[FN.recipientName] = data.recipientName;
    if (data.recipientEmail !== undefined) p[FN.recipientEmail] = data.recipientEmail;
    if (data.sectionsJson !== undefined) p[FN.sectionsJson] = data.sectionsJson;
    if (data.signature !== undefined) p[FN.signature] = data.signature;
    if (data.status !== undefined) p[FN.status] = data.status;
    if (data.acknowledgedAt !== undefined) p[FN.acknowledgedAt] = data.acknowledgedAt;
    try {
      const existing = get().notes.find(n => n.id === id);
      await updateRecord(TABLES.handoffNotes, id, p);
      await get().fetchNotes(existing?.eventId);
    } catch (e) { console.error('[Handoff] updateNote', e); throw e; }
  },

  deleteNote: async (id) => {
    try {
      await deleteRecord(TABLES.handoffNotes, id);
      set({ notes: get().notes.filter(n => n.id !== id) });
    } catch (e) { console.error('[Handoff] deleteNote', e); throw e; }
  },

  submitNote: async (noteId, signature?: string) => {
    await get().updateNote(noteId, {
      status: 'submitted',
      ...(signature ? { signature } : {}),
    });
  },

  acknowledgeNote: async (noteId, recipientName) => {
    await get().updateNote(noteId, {
      status: 'acknowledged',
      recipientName,
      acknowledgedAt: new Date().toISOString(),
    });
  },

  // ── Actions ────────────────────────────────────────

  createAction: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.eventId) p[FA.eventId] = data.eventId;
    if (data.noteId) p[FA.noteId] = data.noteId;
    if (data.title) p[FA.title] = data.title;
    if (data.description) p[FA.description] = data.description;
    if (data.assignedTo) p[FA.assignedTo] = data.assignedTo;
    if (data.assignedToEmail) p[FA.assignedToEmail] = data.assignedToEmail;
    if (data.priority) p[FA.priority] = data.priority;
    p[FA.status] = data.status || 'pending';
    if (data.dueDate) p[FA.dueDate] = data.dueDate;
    if (data.notes) p[FA.notes] = data.notes;
    try {
      const r = await createRecord<Record<string, unknown>>(TABLES.handoffActionItems, p);
      const eventId = data.eventId ? Number(data.eventId) : undefined;
      await get().fetchActions(eventId);
      return (r as any).record_id || (r as any).id || null;
    } catch (e) { console.error('[Handoff] createAction', e); return null; }
  },

  updateAction: async (id, data) => {
    const p: Record<string, unknown> = {};
    if (data.title !== undefined) p[FA.title] = data.title;
    if (data.description !== undefined) p[FA.description] = data.description;
    if (data.assignedTo !== undefined) p[FA.assignedTo] = data.assignedTo;
    if (data.priority !== undefined) p[FA.priority] = data.priority;
    if (data.status !== undefined) p[FA.status] = data.status;
    if (data.dueDate !== undefined) p[FA.dueDate] = data.dueDate;
    if (data.completedAt !== undefined) p[FA.completedAt] = data.completedAt;
    if (data.notes !== undefined) p[FA.notes] = data.notes;
    try {
      const existing = get().actions.find(a => a.id === id);
      await updateRecord(TABLES.handoffActionItems, id, p);
      await get().fetchActions(existing?.eventId);
    } catch (e) { console.error('[Handoff] updateAction', e); throw e; }
  },

  deleteAction: async (id) => {
    try {
      await deleteRecord(TABLES.handoffActionItems, id);
      set({ actions: get().actions.filter(a => a.id !== id) });
    } catch (e) { console.error('[Handoff] deleteAction', e); throw e; }
  },

  completeAction: async (id) => {
    await get().updateAction(id, { status: 'completed', completedAt: new Date().toISOString() });
  },

  reopenAction: async (id) => {
    await get().updateAction(id, { status: 'pending', completedAt: '' });
  },

  // ── Positions ──────────────────────────────────────

  createPosition: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.eventId != null) p[FP.eventId] = Number(data.eventId);
    if (data.positionName) p[FP.positionName] = data.positionName;
    if (data.sortOrder != null) p[FP.sortOrder] = Number(data.sortOrder);
    try {
      const r = await createRecord<Record<string, unknown>>(TABLES.handoffPositions, p);
      const eventId = data.eventId ? Number(data.eventId) : undefined;
      await get().fetchPositions(eventId);
      return (r as any).record_id || (r as any).id || null;
    } catch (e) { console.error('[Handoff] createPosition', e); return null; }
  },

  updatePosition: async (id, data) => {
    const p: Record<string, unknown> = {};
    if (data.positionName !== undefined) p[FP.positionName] = data.positionName;
    if (data.dayShiftName !== undefined) p[FP.dayShiftName] = data.dayShiftName;
    if (data.dayShiftEmail !== undefined) p[FP.dayShiftEmail] = data.dayShiftEmail;
    if (data.nightShiftName !== undefined) p[FP.nightShiftName] = data.nightShiftName;
    if (data.nightShiftEmail !== undefined) p[FP.nightShiftEmail] = data.nightShiftEmail;
    if (data.sortOrder !== undefined) p[FP.sortOrder] = data.sortOrder;
    try {
      const existing = get().positions.find(pos => pos.id === id);
      await updateRecord(TABLES.handoffPositions, id, p);
      await get().fetchPositions(existing?.eventId);
    } catch (e) { console.error('[Handoff] updatePosition', e); throw e; }
  },

  deletePosition: async (id) => {
    try {
      await deleteRecord(TABLES.handoffPositions, id);
      set({ positions: get().positions.filter(p => p.id !== id) });
    } catch (e) { console.error('[Handoff] deletePosition', e); throw e; }
  },

  assignPosition: async (posId, shift, name, email) => {
    const data: Record<string, unknown> = shift === 'day'
      ? { dayShiftName: name, dayShiftEmail: email }
      : { nightShiftName: name, nightShiftEmail: email };
    await get().updatePosition(posId, data);
  },

  clearPositionAssignment: async (posId, shift) => {
    const data: Record<string, unknown> = shift === 'day'
      ? { dayShiftName: '', dayShiftEmail: '' }
      : { nightShiftName: '', nightShiftEmail: '' };
    await get().updatePosition(posId, data);
  },

  // ── Helpers ────────────────────────────────────────

  getPositionsForEvent: (eventId) => {
    const eid = Number(eventId);
    return get().positions.filter(p => p.eventId === eid).sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
  },

  getNotesForEvent: (eventId) => {
    const eid = Number(eventId);
    return get().notes.filter(n => n.eventId === eid);
  },

  getActionsForEvent: (eventId) => {
    const eid = Number(eventId);
    return get().actions.filter(a => a.eventId === eid);
  },

  // ── AI ─────────────────────────────────────────────

  generateAISummary: async (note) => {
    const content = note.sections.map(s => `## ${s.title}\n${s.content}`).join('\n\n');
    const result = await callClaude(
      [{ role: 'user', content: `Analyze this shift handoff note and provide a structured summary.\n\nPosition: ${note.positionName}\nShift: ${note.fromShift} → ${note.toShift}\nAuthor: ${note.senderName}\n\n${content}\n\nRespond in JSON format:\n{"summary": "...", "keyPoints": ["..."], "urgentItems": ["..."]}` }],
      { system: 'You are an emergency management AI assistant. Analyze shift handoff notes and extract key information. Respond ONLY in valid JSON.', maxTokens: 800 }
    );
    // Store AI summary in memory on the note object
    set({
      notes: get().notes.map(n =>
        n.id === note.id ? { ...n, aiSummary: result } : n
      ),
    });
    return result;
  },

  generateActionsFromHandoff: async (note) => {
    const content = note.sections.map(s => `## ${s.title}\n${s.content}`).join('\n\n');
    const result = await callClaude(
      [{ role: 'user', content: `Based on this shift handoff note, identify action items that the incoming shift should follow up on.\n\nPosition: ${note.positionName}\nShift: ${note.fromShift} → ${note.toShift}\n\n${content}\n\nRespond in JSON array format:\n[{"title": "...", "description": "...", "priority": "low|medium|high|critical", "assignee": "..."}]` }],
      { system: 'You are an emergency management AI assistant. Extract actionable items from shift handoff notes. Respond ONLY in valid JSON array format.', maxTokens: 800 }
    );
    return result;
  },

  // V1 compat aliases
  actionItems: [] as any[],
  isLoading: false,
  rosterUsers: [] as any[],
  loadNotes: async (eventId?: number) => { await get().fetchNotes(eventId); },
  loadActionItems: async (eventId?: number) => { await get().fetchActions(eventId); },
  loadPositions: async (eventId?: number) => { await get().fetchPositions(eventId); },
  loadRosterUsers: async () => {
    try {
      const users = await fetchIgnatiusUsers();
      set({ rosterUsers: users });
    } catch (e) { console.error('[Handoff] loadRosterUsers', e); }
  },
  createActionItem: async (data: any) => { return await get().createAction(data); },
  deleteActionItem: async (id: any) => { await get().deleteAction(id); },
  completeActionItem: async (id: any) => { await get().completeAction(id); },
  reopenActionItem: async (id: any) => { await get().reopenAction(id); },
  generateBriefing: async (eventData: any, notes: any[]) => {
    try {
      const summary = notes.slice(0, 5).map((n: any) =>
        `[${n.positionName} - ${n.fromShift} shift] ${n.sections ? JSON.parse(n.sectionsJson || '[]').map((s: any) => s.content).join(' ') : ''}`
      ).join('\n');
      return await callClaude([{ role: 'user', content: `Generate a concise operational briefing summary for shift handoff. Event: ${JSON.stringify(eventData)}. Recent notes:\n${summary}\n\nProvide: executive summary, key issues, staffing status. Be concise.` }], { maxTokens: 500 });
    } catch { return ''; }
  },
  generatePriorities: async (_eventId: any) => [],
  loadAll: async () => {
    await get().fetchEvents(); await get().fetchNotes(); await get().fetchActions(); await get().fetchPositions();
    await get().loadRosterUsers();
  },
  saveDraft: async (data: any) => {
    const sectionsJson = JSON.stringify(data.sections || []);
    if (data.noteId) {
      await get().updateNote(data.noteId, {
        eventId: data.eventId, positionName: data.positionName,
        fromShift: data.fromShift, toShift: data.toShift,
        senderName: data.senderName, shiftDate: data.shiftDate,
        sectionsJson, status: 'draft',
      });
      return { noteId: data.noteId };
    } else {
      const id = await get().createNote({
        eventId: data.eventId, positionName: data.positionName,
        fromShift: data.fromShift, toShift: data.toShift,
        senderName: data.senderName, shiftDate: data.shiftDate,
        sectionsJson, status: 'draft',
      });
      return { noteId: id };
    }
  },
}));
